import React, { useEffect, useState } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { FeatherKey } from "@subframe/core";
import {
  OAuthClientDTO,
  getBotMailSettings,
  saveBotMailSettings,
} from "../../data/api/botMailApi";

interface MailSettingsCardProps {
  /** Open by default when nothing is configured yet. */
  defaultOpen?: boolean;
  onSaved: () => void;
}

/**
 * The one-time Google Cloud setup: which OAuth app the mailboxes consent
 * through. Stored in bot_oauth_clients (secret encrypted), NOT in env — so
 * rotating credentials or moving environments is an admin action.
 *
 * The secret field stays blank on an update; leaving it blank keeps the stored
 * one, so changing a URL doesn't mean re-pasting the secret.
 */
export const MailSettingsCard: React.FC<MailSettingsCardProps> = ({
  defaultOpen = false,
  onSaved,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const [client, setClient] = useState<OAuthClientDTO | null>(null);
  const [suggestedRedirect, setSuggestedRedirect] = useState("");
  const [hostMatches, setHostMatches] = useState(true);
  const [form, setForm] = useState({
    label: "",
    client_id: "",
    client_secret: "",
    redirect_uri: "",
    app_return_url: "",
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const res = await getBotMailSettings();
        if (cancelled) return;

        const existing = res.data.oauth_client;
        setClient(existing);
        setSuggestedRedirect(res.data.suggested_redirect_uri);
        setHostMatches(res.data.redirect_uri_matches_host);
        // A host mismatch is actionable and easy to miss — open the card so the
        // warning and the field to fix it are both visible.
        if (existing && !res.data.redirect_uri_matches_host) setOpen(true);
        setForm({
          label: existing?.label ?? "",
          client_id: existing?.client_id ?? "",
          client_secret: "",
          redirect_uri: existing?.redirect_uri ?? res.data.suggested_redirect_uri,
          app_return_url:
            existing?.app_return_url ??
            `${window.location.origin}/admin/dashboard?tab=zoho-bot`,
        });
        if (!existing) setOpen(true);
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    if (!form.client_id.trim() || !form.redirect_uri.trim()) {
      setError("Client ID and redirect URI are both required.");
      return;
    }
    if (!client?.has_client_secret && !form.client_secret.trim()) {
      setError("Client secret is required the first time.");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSaved(null);
      const res = await saveBotMailSettings({
        label: form.label.trim() || undefined,
        client_id: form.client_id.trim(),
        client_secret: form.client_secret.trim() || undefined,
        redirect_uri: form.redirect_uri.trim(),
        app_return_url: form.app_return_url.trim() || undefined,
      });
      setClient(res.data.oauth_client);
      setForm((prev) => ({ ...prev, client_secret: "" }));
      setHostMatches(
        form.redirect_uri.trim().replace(/\/$/, "") ===
          suggestedRedirect.replace(/\/$/, "")
      );
      setSaved("Google OAuth app saved. You can connect a mailbox now.");
      onSaved();
    } catch (err: any) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100">
            <FeatherKey className="h-5 w-5 text-brand-600" />
          </div>
          <div className="flex flex-col items-start gap-1">
            <div className="flex items-center gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Google connection
              </span>
              {loading ? (
                <Badge variant="neutral">Checking…</Badge>
              ) : !client ? (
                <Badge variant="warning">Not set up</Badge>
              ) : hostMatches ? (
                <Badge variant="success">Configured</Badge>
              ) : (
                <Badge variant="error">Wrong host</Badge>
              )}
            </div>
            <span className="text-body font-body text-subtext-color">
              One-time setup. The same OAuth app authorises every mailbox you add.
            </span>
          </div>
        </div>
        <Button variant="neutral-secondary" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : client ? "Edit" : "Set up"}
        </Button>
      </div>

      {open && (
        <>
          {client && !hostMatches && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-error-200 bg-error-50 p-4">
              <span className="text-body-bold font-body-bold text-error-700">
                The saved redirect URI is for a different host.
              </span>
              <span className="text-body font-body text-error-700">
                Saved: <code className="break-all">{client.redirect_uri}</code>
                <br />
                This host: <code className="break-all">{suggestedRedirect}</code>
              </span>
              <span className="text-body font-body text-error-700">
                Connecting a mailbox will fail with{" "}
                <code>redirect_uri_mismatch</code> until this matches a URI
                registered in Google Cloud. Add this host's URI there, then switch
                to it here.
              </span>
              <Button
                variant="neutral-secondary"
                onClick={() =>
                  setForm({ ...form, redirect_uri: suggestedRedirect })
                }
              >
                Use this host
              </Button>
            </div>
          )}

          <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
            <span className="text-body-bold font-body-bold text-default-font">
              In Google Cloud, once:
            </span>
            <ol className="flex list-decimal flex-col items-start gap-1 pl-5">
              <li className="text-body font-body text-subtext-color">
                APIs &amp; Services → Library → enable <b>Gmail API</b>.
              </li>
              <li className="text-body font-body text-subtext-color">
                OAuth consent screen → <b>Internal</b> (notionpress.com Workspace) →
                add scopes <code>gmail.modify</code> and <code>userinfo.email</code>.
              </li>
              <li className="text-body font-body text-subtext-color">
                Credentials → Create → <b>OAuth client ID</b> → <b>Web application</b>.
              </li>
              <li className="text-body font-body text-subtext-color">
                Add this exact Authorised redirect URI:{" "}
                <code className="break-all">{suggestedRedirect || "…"}</code>
                <br />
                If this deployment serves the API under a path prefix, include
                it — the server cannot see a prefix the proxy strips. On dev
                that means{" "}
                <code className="break-all">
                  …zournalhq.com/backend/oauth/google/callback
                </code>
                .
              </li>
              <li className="text-body font-body text-subtext-color">
                Paste the client ID and secret below.
              </li>
            </ol>
          </div>

          <div className="flex w-full flex-wrap items-start gap-4">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Client ID"
              helpText="Ends in .apps.googleusercontent.com"
            >
              <TextField.Input
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                placeholder="1234-abc.apps.googleusercontent.com"
              />
            </TextField>
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Client secret"
              helpText={
                client?.has_client_secret
                  ? "Already stored (encrypted). Leave blank to keep it."
                  : "Stored encrypted — never returned by the API."
              }
            >
              <TextField.Input
                type="password"
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                placeholder={client?.has_client_secret ? "••••••••" : "GOCSPX-…"}
              />
            </TextField>
          </div>

          <div className="flex w-full flex-wrap items-start gap-4">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Authorised redirect URI"
              helpText="Must match Google Cloud character for character."
            >
              <TextField.Input
                value={form.redirect_uri}
                onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })}
              />
            </TextField>
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Return to (admin UI)"
              helpText="Where Google's callback sends you back after consent."
            >
              <TextField.Input
                value={form.app_return_url}
                onChange={(e) => setForm({ ...form, app_return_url: e.target.value })}
              />
            </TextField>
          </div>

          <TextField className="h-auto w-full" label="Label (optional)">
            <TextField.Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="NP Zoho Bot — GCP project np-bots"
            />
          </TextField>

          {error && (
            <span className="text-body font-body text-error-700">{error}</span>
          )}
          {saved && (
            <span className="text-body font-body text-success-700">{saved}</span>
          )}

          <div className="flex items-center gap-2">
            <Button loading={saving} onClick={handleSave}>
              Save Google app
            </Button>
            {client?.updated_at && (
              <span className="text-caption font-caption text-subtext-color">
                Last saved {new Date(client.updated_at).toLocaleString()}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MailSettingsCard;
